import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Layers,
  ClipboardList,
  FileText,
  MessageSquare,
  Users,
  Shield,
  Building2,
  Briefcase,
  Settings,
  CheckCircle2,
  ChevronDown,
  Lock,
} from 'lucide-react';
import { useMenu } from '../../Common/MenuContext';
import { useMenuState } from '../../Common/MenuStateContext';
import { useAuth } from '../../auth/AuthContext';
import { HOME_PATH } from '../../constants/routes';
import { partitionSidebarMenuItems, resolveMenuPathForUser } from '../../utils/menuRouteUtils';
import { resolveMenuDisplayLabel } from '../../utils/rolePermissionsUtils';
import { getPlatformLogoSrc } from '../../constants/branding';
import '../../styles/shell.css';

type LucideIcon = React.ComponentType<{ className?: string; size?: number }>;

const lucideByKey: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  settings: Settings,
  project: FolderKanban,
  projects: FolderKanban,
  capabilities: Layers,
  capability: Layers,
  assessment: ClipboardList,
  assessments: ClipboardList,
  import_export: ClipboardList,
  reports: FileText,
  report: FileText,
  chat: MessageSquare,
  user: Users,
  users: Users,
  user_management: Users,
  role: Shield,
  roles: Shield,
  role_management: Shield,
  company: Building2,
  companies: Building2,
  company_management: Building2,
  business_type: Briefcase,
  user_type: Briefcase,
  business: Briefcase,
  capability_management: Layers,
  industry: Settings,
  industry_management: Settings,
  framework: Settings,
  frameworks: Settings,
  framework_management: Settings,
  masterlist: Briefcase,
  master_list: Briefcase,
  traxccelframeworks: Layers,
  assessmentblueprints: ClipboardList,
  assessment_blueprints: ClipboardList,
  sector: Settings,
  sector_management: Settings,
  functions: Settings,
  functions_management: Settings,
  data: Settings,
  data_management: Settings,
  rights: Shield,
  rights_management: Shield,
  project_management: FolderKanban,
  project_activity: FolderKanban,
  project_assessments: ClipboardList,
  project_chats: MessageSquare,
  project_reports: FileText,
  capability_approval: CheckCircle2,
  approval: CheckCircle2,
  business_value_drivers: Settings,
  functional_value_drivers: Settings,
  use_cases: Settings,
  team: Users,
  team_member: Users,
  team_management: Users,
  cap_assess: ClipboardList,
  assessment_tracking: ClipboardList,
  tracking: ClipboardList,
  value_estimation: FileText,
  organization: Building2,
  access: Users,
  requests: CheckCircle2,
  approvals: CheckCircle2,
  default: Settings,
};

function resolveLucide(menuName?: string): LucideIcon {
  if (!menuName) return lucideByKey.default;
  const key = menuName.toLowerCase().replace(/\s+/g, '_');
  if (lucideByKey[key]) return lucideByKey[key];
  const spaced = menuName.toLowerCase();
  if (lucideByKey[spaced]) return lucideByKey[spaced];
  for (const [k, Icon] of Object.entries(lucideByKey)) {
    if (spaced.includes(k.replace(/_/g, ' ')) || key.includes(k)) return Icon;
  }
  return lucideByKey.default;
}

const isDesktopViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 992px)').matches;

interface MenuProps {
  onSelect?: (item: string) => void;
  selectedItem?: string | null;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

function Menu({ onSelect, isCollapsed = false, onToggleCollapse }: MenuProps) {
  const basename = process.env.PUBLIC_URL || '';
  const {
    menuItems,
    loadingMenu,
    hasUnreadChat,
    hasUnreadReport,
    hasPendingCapabilityApprovals,
    hasPendingProjectSetup,
    clientAssessmentNavLocked,
    clientReportsNavLocked,
    hasNewAssessmentNav,
    hasUnopenedReportsNav,
  } = useMenu();
  const { closeMobileMenu } = useMenuState();
  const { user } = useAuth();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(location.pathname);
  const [profile, setProfile] = useState<any[] | null>(null);
  const [openDropdowns, setOpenDropdowns] = useState(new Set<number | string>());
  const [userRole, setUserRole] = useState<string | null>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  const getUserRole = useCallback(() => {
    if (user) {
      return user.roleName?.toLowerCase() || user.userType?.toLowerCase() || 'client';
    }
    return null;
  }, [user]);

  useEffect(() => {
    setActiveMenu(location.pathname);
    const currentPath = location.pathname;
    const ancestorIds: Array<number | string> = [];

    const collectAncestors = (items: any[], ancestors: Array<number | string> = []) => {
      if (!Array.isArray(items)) return;
      for (const item of items) {
        const itemPath = resolveMenuPathForUser(item, userRole);
        if (itemPath && itemPath === currentPath) {
          ancestorIds.push(...ancestors);
        }
        if (item.subMenus?.length) {
          collectAncestors(item.subMenus, [...ancestors, item.id]);
        }
      }
    };

    collectAncestors(menuItems);
    if (ancestorIds.length > 0) {
      setOpenDropdowns((prev) => new Set([...prev, ...ancestorIds]));
    }
  }, [location, menuItems, userRole]);

  useEffect(() => {
    const selectedProjectId = localStorage.getItem('projectId');
    if (user) {
      const selectedProjects = (user.projectDetails ?? []).filter(
        (f: any) => f.projectId == selectedProjectId
      );
      setProfile(selectedProjects);
    }
    setUserRole(getUserRole());
  }, [user, getUserRole]);

  const handleMenuClick = useCallback(
    (menu: string) => {
      setActiveMenu(menu);
      onSelect?.(menu);
      closeMobileMenu();
    },
    [onSelect, closeMobileMenu]
  );

  const toggleDropdown = useCallback(
    (menuId: number | string) => {
      // A collapsed rail has no room for submenus, so expand it and open the group
      if (isCollapsed && onToggleCollapse && isDesktopViewport()) {
        onToggleCollapse();
        setOpenDropdowns((prev) => new Set([...prev, menuId]));
        return;
      }
      setOpenDropdowns((prev) => {
        const next = new Set(prev);
        if (next.has(menuId)) next.delete(menuId);
        else next.add(menuId);
        return next;
      });
    },
    [isCollapsed, onToggleCollapse]
  );

  const isDropdownOpen = useCallback(
    (menuId: number | string) => openDropdowns.has(menuId),
    [openDropdowns]
  );

  const isChatMenuItem = useCallback((menuName: string, itemPath: string) => {
    const name = String(menuName || '').toLowerCase();
    const path = String(itemPath || '').toLowerCase();
    return name.includes('chat') || path.includes('chat');
  }, []);

  const isReportMenuItem = useCallback((menuName: string, itemPath: string) => {
    const name = String(menuName || '').toLowerCase();
    const path = String(itemPath || '').toLowerCase();
    return name.includes('report') || path.includes('report');
  }, []);

  const isClientPortalNavPath = useCallback(
    (itemPath: string | null | undefined, target: '/assessment' | '/reports') => {
      const basePath = String(itemPath || '').split('?')[0];
      return basePath === target;
    },
    []
  );

  const getClientNavLock = useCallback(
    (itemPath: string | null | undefined): 'assessment' | 'reports' | null => {
      if (!itemPath) return null;
      const basePath = String(itemPath).split('?')[0];
      if (basePath === '/assessment' && clientAssessmentNavLocked) {
        return 'assessment';
      }
      if (basePath === '/reports' && clientReportsNavLocked) {
        return 'reports';
      }
      return null;
    },
    [clientAssessmentNavLocked, clientReportsNavLocked]
  );

  const getClientNavLockTitle = useCallback((lock: 'assessment' | 'reports') => {
    if (lock === 'assessment') {
      return 'Select capabilities to unlock assessment';
    }
    return 'Reports unlock when your advisor submits a report';
  }, []);

  const isCapabilityApprovalMenuItem = useCallback((menuName: string, itemPath: string) => {
    const name = String(menuName || '').toLowerCase();
    const path = String(itemPath || '').toLowerCase();
    return (
      path.includes('capability_approval') ||
      name.includes('capability approval') ||
      name.includes('capability request')
    );
  }, []);

  const isProjectSetupApprovalMenuItem = useCallback((menuName: string, itemPath: string) => {
    const name = String(menuName || '').toLowerCase();
    const path = String(itemPath || '').toLowerCase();
    return path.includes('project_setup_approval') || name.includes('project setup');
  }, []);

  const getMenuAlert = useCallback(
    (menuName: string, itemPath: string) => {
      if (Boolean(hasUnreadChat) && isChatMenuItem(menuName, itemPath)) {
        return { show: true, title: 'New chat message' };
      }
      if (
        hasNewAssessmentNav &&
        isClientPortalNavPath(itemPath, '/assessment')
      ) {
        return { show: true, title: 'Assessment is ready' };
      }
      if (
        !clientReportsNavLocked &&
        (Boolean(hasUnreadReport) || Boolean(hasUnopenedReportsNav)) &&
        isClientPortalNavPath(itemPath, '/reports')
      ) {
        return {
          show: true,
          title: hasUnreadReport ? 'New report available' : 'Reports are ready',
        };
      }
      if (
        Boolean(hasPendingCapabilityApprovals) &&
        isCapabilityApprovalMenuItem(menuName, itemPath)
      ) {
        return { show: true, title: 'Pending capability requests' };
      }
      if (
        Boolean(hasPendingProjectSetup) &&
        isProjectSetupApprovalMenuItem(menuName, itemPath)
      ) {
        return { show: true, title: 'Pending project setup requests' };
      }
      return { show: false, title: '' };
    },
    [
      hasUnreadChat,
      hasUnreadReport,
      hasNewAssessmentNav,
      hasUnopenedReportsNav,
      clientReportsNavLocked,
      hasPendingCapabilityApprovals,
      hasPendingProjectSetup,
      isChatMenuItem,
      isClientPortalNavPath,
      isCapabilityApprovalMenuItem,
      isProjectSetupApprovalMenuItem,
    ]
  );

  const getItemOrDescendantAlert = useCallback(
    (item: any) => {
      if (!item) return { show: false, title: '' };
      const iconSourceName = item.menuName || item.displayName || '';
      const itemPath = resolveMenuPathForUser(item, userRole);
      const selfAlert = getMenuAlert(iconSourceName, itemPath);
      if (selfAlert.show) return selfAlert;

      const pendingTitles: string[] = [];
      const visit = (node: any) => {
        if (!node) return;
        const childName = node.menuName || node.displayName || '';
        const childPath = resolveMenuPathForUser(node, userRole);
        const childAlert = getMenuAlert(childName, childPath);
        if (childAlert.show && childAlert.title) pendingTitles.push(childAlert.title);
        (Array.isArray(node.subMenus) ? node.subMenus : []).forEach(visit);
      };
      (Array.isArray(item.subMenus) ? item.subMenus : []).forEach(visit);

      if (pendingTitles.length === 0) return { show: false, title: '' };
      const uniqueTitles = [...new Set(pendingTitles)];
      const hasCapability = uniqueTitles.some((t) => t.toLowerCase().includes('capability'));
      const hasProjectSetup = uniqueTitles.some((t) => t.toLowerCase().includes('project setup'));
      let title = uniqueTitles[0];
      if (hasCapability && hasProjectSetup) title = 'Pending approval requests';
      else if (uniqueTitles.length > 1) title = 'New items available';
      return { show: true, title };
    },
    [getMenuAlert, userRole]
  );

  const itemClass = (
    active: boolean,
    nested: boolean,
    alert = false,
    locked = false
  ) =>
    [
      'sb__item',
      nested ? 'sb__item--nested' : '',
      active && !locked ? 'sb__item--active' : '',
      alert && !locked ? 'sb__item--alert' : '',
      locked ? 'sb__item--locked' : '',
    ]
      .filter(Boolean)
      .join(' ');

  const hasActiveDescendant = useCallback(
    (node: any): boolean => {
      const subs = Array.isArray(node?.subMenus) ? node.subMenus : [];
      for (const sub of subs) {
        const subPath = resolveMenuPathForUser(sub, userRole);
        if (subPath && activeMenu === subPath) return true;
        if (hasActiveDescendant(sub)) return true;
      }
      return false;
    },
    [activeMenu, userRole]
  );

  const renderMenuItem = useCallback(
    (item: any, level = 0) => {
      if (item.isCategory) {
        const groupLabel = resolveMenuDisplayLabel(item.menuName, item.displayName);
        const children = Array.isArray(item.subMenus) ? item.subMenus : [];
        if (!groupLabel && children.length === 0) return null;

        const categoryId = item.id ?? groupLabel;
        const isOpen = isDropdownOpen(categoryId);
        const categoryHighlighted = isOpen || hasActiveDescendant(item);
        const showChildren = isOpen && !(isCollapsed && isDesktopViewport());

        return (
          <div key={categoryId} className="sb__category-block">
            {groupLabel ? (
              <button
                type="button"
                className={[
                  'sb__category',
                  categoryHighlighted ? 'sb__category--open' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => toggleDropdown(categoryId)}
                aria-expanded={isOpen}
              >
                <span className="sb__category-label">{groupLabel}</span>
                <ChevronDown
                  className={`sb__chevron${isOpen ? ' sb__chevron--open' : ''}`}
                  size={14}
                />
              </button>
            ) : null}
            {showChildren
              ? children.map((subItem: any) => renderMenuItem(subItem, level))
              : null}
          </div>
        );
      }

      const hasSubMenus = item.subMenus && item.subMenus.length > 0;
      const isOpen = isDropdownOpen(item.id);
      const itemPath = resolveMenuPathForUser(item, userRole);
      const isActive = itemPath ? activeMenu === itemPath : false;
      const isParentHighlighted =
        hasSubMenus && (isOpen || isActive || hasActiveDescendant(item));
      const isHighlighted = hasSubMenus ? isParentHighlighted : isActive;
      const iconSourceName = item.menuName || item.displayName || '';
      const menuName = resolveMenuDisplayLabel(item.menuName, item.displayName);
      const menuAlert = getItemOrDescendantAlert(item);
      const Icon = resolveLucide(iconSourceName);
      const nested = level > 0;

      const badge = menuAlert.show ? (
        <span className="sb__badge" title={menuAlert.title}>
          <span className="sb__badge-dot" />
          <span className="sb__badge-text">New</span>
        </span>
      ) : null;

      if (hasSubMenus) {
        return (
          <div key={item.id}>
            <button
              type="button"
              className={itemClass(isHighlighted, nested, menuAlert.show)}
              onClick={(e) => {
                e.preventDefault();
                toggleDropdown(item.id);
              }}
              aria-expanded={isOpen}
              title={menuAlert.show ? menuAlert.title : menuName}
            >
              <Icon className="sb__item-icon" size={18} strokeWidth={1.75} />
              <span className="sb__item-label">{menuName}</span>
              {badge}
              <ChevronDown
                className={`sb__chevron${isOpen ? ' sb__chevron--open' : ''}`}
                size={14}
              />
            </button>
            {isOpen && !(isCollapsed && isDesktopViewport()) ? (
              <div className="sb__submenu">
                {item.subMenus.map((subItem: any) => {
                  if (Array.isArray(subItem.subMenus) && subItem.subMenus.length > 0) {
                    return renderMenuItem(subItem, level + 1);
                  }
                  const subIconSourceName = subItem.menuName || subItem.displayName || '';
                  const subMenuName = resolveMenuDisplayLabel(
                    subItem.menuName,
                    subItem.displayName
                  );
                  const subItemPath = resolveMenuPathForUser(subItem, userRole);
                  const isSubActive = subItemPath ? activeMenu === subItemPath : false;
                  const subAlert = getItemOrDescendantAlert(subItem);
                  const SubIcon = resolveLucide(subIconSourceName);
                  if (!subItemPath) return null;
                  const subLock = getClientNavLock(subItemPath);
                  const subTitle = subLock
                    ? getClientNavLockTitle(subLock)
                    : subAlert.show
                      ? subAlert.title
                      : subMenuName;
                  if (subLock) {
                    return (
                      <span
                        key={subItem.id}
                        className={itemClass(false, true, false, true)}
                        title={subTitle}
                        aria-disabled="true"
                      >
                        <SubIcon className="sb__item-icon" size={18} strokeWidth={1.75} />
                        <span className="sb__item-label">{subMenuName}</span>
                        <Lock className="sb__item-lock" size={16} strokeWidth={1.75} aria-hidden />
                      </span>
                    );
                  }
                  return (
                    <Link
                      to={subItemPath}
                      key={subItem.id}
                      onClick={() => handleMenuClick(subItemPath)}
                      className={itemClass(isSubActive, true, subAlert.show)}
                      title={subTitle}
                    >
                      <SubIcon className="sb__item-icon" size={18} strokeWidth={1.75} />
                      <span className="sb__item-label">{subMenuName}</span>
                      {subAlert.show ? <span className="sb__dot" title={subAlert.title} /> : null}
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      }

      if (!itemPath) return null;

      const navLock = getClientNavLock(itemPath);
      const itemTitle = navLock
        ? getClientNavLockTitle(navLock)
        : menuAlert.show
          ? menuAlert.title
          : menuName;

      if (navLock) {
        return (
          <span
            key={item.id}
            className={itemClass(false, nested, false, true)}
            title={itemTitle}
            aria-disabled="true"
          >
            <Icon className="sb__item-icon" size={18} strokeWidth={1.75} />
            <span className="sb__item-label">{menuName}</span>
            <Lock className="sb__item-lock" size={16} strokeWidth={1.75} aria-hidden />
          </span>
        );
      }

      return (
        <Link
          key={item.id}
          to={itemPath}
          onClick={() => handleMenuClick(itemPath)}
          className={itemClass(isActive, nested, menuAlert.show)}
          title={itemTitle}
        >
          <Icon className="sb__item-icon" size={18} strokeWidth={1.75} />
          <span className="sb__item-label">{menuName}</span>
          {badge}
        </Link>
      );
    },
    [
      isCollapsed,
      isDropdownOpen,
      toggleDropdown,
      activeMenu,
      handleMenuClick,
      userRole,
      getItemOrDescendantAlert,
      hasActiveDescendant,
      getClientNavLock,
      getClientNavLockTitle,
    ]
  );

  const profileProjects = useMemo(() => profile || [], [profile]);
  const { mainMenuItems, settingsMenuItem } = useMemo(
    () => partitionSidebarMenuItems(menuItems, userRole),
    [menuItems, userRole]
  );
  const isDesktop = isDesktopViewport();
  const showBrandText = !isCollapsed || !isDesktop;

  const isClientRole = userRole === 'client';

  return (
    <div className={isClientRole ? 'sb sb--client' : 'sb'}>
      <div className="sb__header">
        <Link to={HOME_PATH} className="sb__brand" aria-label="Go to home page" title="Go to home page">
          <img
            className="sb__brand-logo"
            alt="Traxccel Logo"
            src={getPlatformLogoSrc('small', basename)}
          />
          {showBrandText ? <span className="sb__brand-name">ReImagine</span> : null}
        </Link>
      </div>

      <nav ref={menuContainerRef} className="sb__nav" aria-label="Main navigation">
        {loadingMenu ? (
          <p className="sb__status">Loading menu…</p>
        ) : mainMenuItems.length === 0 && !settingsMenuItem ? (
          <p className="sb__status">No menu items available</p>
        ) : (
          mainMenuItems.map((item: any) => renderMenuItem(item))
        )}
      </nav>

      {settingsMenuItem ? (
        <div className="sb__footer-nav" aria-label="Settings">
          {renderMenuItem(settingsMenuItem)}
        </div>
      ) : null}

      {isClientRole ? (
        <div className="sb__projects sb__projects--client">
          <div className="sb__project-panel sb__project-panel--client">
            <p className="sb__project-eyebrow sb__project-eyebrow--client">Current project</p>
            {profileProjects.length === 0 ? (
              <p className="sb__project-name sb__project-name--client">No project selected</p>
            ) : (
              profileProjects.map((project: any) => (
                <div key={project.projectId} className="sb__project-body">
                  <p className="sb__project-name sb__project-name--client">
                    {project.projectName}
                  </p>
                  {project.companyName ? (
                    <p className="sb__project-company sb__project-company--client">
                      {project.companyName}
                    </p>
                  ) : null}
                </div>
              ))
            )}
            <button
              type="button"
              className="sb__change-link"
              onClick={() => {
                closeMobileMenu();
                window.location.href = `${basename}/project`;
              }}
              title="Switch to a different project"
            >
              Change project
            </button>
          </div>
        </div>
      ) : null}

      {!isClientRole ? (
        <div className="sb__user">
          <div className="sb__avatar">{(user?.userName || 'U').charAt(0).toUpperCase()}</div>
          <div className="sb__user-meta">
            <p className="sb__user-name">{user?.userName || '—'}</p>
            <p className="sb__user-role">{user?.jobTitle || user?.roleName || '—'}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default React.memo(Menu);
